import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import Skeleton from '@/components/ui/Skeleton'
import { failureMessage, portfolioFailure } from '@/features/portfolio/api/errors'
import { useAnalytics, usePortfolioOverview, useProfitLoss } from '@/features/portfolio/api/queries'
import UpgradeRequired from '@/features/subscriptions/components/UpgradeRequired'
import { subscriptionRequiredFeature } from '@/features/subscriptions/lib/paywall'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState, savedAtMeta, type ViewSource } from '@/lib/view-state'
import type { PortfolioOverview, ProfitLossReport } from '@/schemas/portfolio'
import ContributorsCard from '@/screens/portfolio/components/analytics/ContributorsCard'
import {
  contributors,
  sectorContribution,
  totalReturnFromBreakdown,
} from '@/screens/portfolio/components/analytics/deriveAnalytics'
import ReturnMeasuresCard from '@/screens/portfolio/components/analytics/ReturnMeasuresCard'
import SectorContributionTable from '@/screens/portfolio/components/analytics/SectorContributionTable'
import TotalReturnCard from '@/screens/portfolio/components/analytics/TotalReturnCard'
import PortfolioFamilyHeader from '@/screens/portfolio/components/PortfolioFamilyHeader'
import { useActiveShareholderId } from '@/screens/portfolio/components/portfolio-switcher/activeProfile'

export const Route = createFileRoute('/_app/portfolio/analytics')({ component: AnalyticsScreen })

interface OverviewAndProfitLoss {
  overview: PortfolioOverview
  profitLoss: ProfitLossReport
}

/**
 * Portfolio · Analytics (V2 `4236:77871`, V1 997:28241).
 *
 * The one tab in this feature that asks for more than the API can supply —
 * see the delivery report for the full accounting. In short:
 *
 *  - Realised/unrealised P/L, dividends, contributors and sector contribution
 *    are all real, built from `GET /overview/` (`summary` + `holdings`) and
 *    `GET /profit-loss/` (`profitLoss.totalRealized`) — see
 *    `components/analytics/deriveAnalytics.ts`.
 *  - XIRR, TWR and turnover are READ from `GET /analytics/`, never derived
 *    here — see `ReturnMeasuresCard`'s docblock. That third read does not
 *    join the gate below; the card carries its own pending/error state and
 *    its own retry (`onRetry`/`retrying` below), independent of the tab's.
 *
 * `GET /transactions/` is not read here at all. It fed only the XIRR
 * cash-flow series and the turnover denominator, both of which are the
 * server's to compute.
 *
 * ── View state over TWO queries, via `resolveViewState` directly ───────────
 *
 * `overview` and `profitLoss` are awaited TOGETHER (unlike `OverviewScreen`'s
 * independent `profitLoss` gate) because this screen's headline figures are
 * SUMS across `summary` and `profitLoss` — there is no single-endpoint subset
 * of this page that renders meaningfully on its own the way Overview's five
 * stat cells do. `QueryState` is built for exactly one query, so this hands
 * `resolveViewState` a small synthetic `ViewSource` combining both — the same
 * ordering (data-on-screen wins, then offline, then failed) it gives a single
 * query, so a background refetch failing on either one keeps the page
 * standing, and a first load with no connection reads as `offline` rather
 * than loading forever.
 */
function AnalyticsScreen() {
  const shareholderId = useActiveShareholderId()
  const overviewQuery = usePortfolioOverview(shareholderId)
  const profitLossQuery = useProfitLoss(shareholderId)
  // XIRR / TWR / Turnover only. NOT part of the gate below — it feeds one
  // card, which carries its own pending, error, and retry.
  const analyticsQuery = useAnalytics(shareholderId)
  const online = useOnline()

  const combined: ViewSource<OverviewAndProfitLoss> = {
    data:
      overviewQuery.data === undefined || profitLossQuery.data === undefined
        ? undefined
        : { overview: overviewQuery.data, profitLoss: profitLossQuery.data },
    isError: overviewQuery.isError || profitLossQuery.isError,
    fetchStatus:
      overviewQuery.fetchStatus === 'fetching' || profitLossQuery.fetchStatus === 'fetching'
        ? 'fetching'
        : overviewQuery.fetchStatus === 'paused' || profitLossQuery.fetchStatus === 'paused'
          ? 'paused'
          : 'idle',
    errorUpdateCount: Math.max(overviewQuery.errorUpdateCount, profitLossQuery.errorUpdateCount),
  }
  const view = resolveViewState(combined, {
    isEmpty: ({ overview }) => overview.holdings.length === 0,
    online,
  })
  const retryBoth = () => {
    void overviewQuery.refetch()
    void profitLossQuery.refetch()
  }
  const retryingBoth = overviewQuery.isFetching || profitLossQuery.isFetching

  const paywall = subscriptionRequiredFeature(profitLossQuery.error)
  let body: ReactNode

  if (view === 'loading') {
    body = (
      <>
        <Skeleton className="h-48 w-full rounded-lg" />
        <div className="grid grid-cols-1 gap-5 @2xl:grid-cols-2">
          <Skeleton className="h-72 rounded-lg" />
          <Skeleton className="h-72 rounded-lg" />
        </div>
        <Skeleton className="h-40 w-full rounded-lg" />
      </>
    )
  } else if (paywall !== null && view === 'failed') {
    // `profit-loss/` is gated on `portfolio.pnl_visualisation` (CONTRACT §7).
    body = <UpgradeRequired featureKey={paywall} />
  } else if (view === 'failed' || view === 'offline') {
    const failure =
      portfolioFailure(overviewQuery.error) ??
      portfolioFailure(profitLossQuery.error) ??
      ({ kind: 'failed' } as const)
    body = (
      <EmptyState
        kind={view}
        size="page"
        // Offline keeps EmptyState's own "You are offline" copy — see the
        // matching comment in HoldingsScreen.
        {...(view === 'failed'
          ? { title: 'Analytics did not load', description: failureMessage(failure) }
          : {})}
        onRetry={retryBoth}
        retrying={retryingBoth}
        meta={
          view === 'failed'
            ? lastUpdatedMeta(Math.max(overviewQuery.dataUpdatedAt, profitLossQuery.dataUpdatedAt))
            : savedAtMeta(Math.max(overviewQuery.dataUpdatedAt, profitLossQuery.dataUpdatedAt))
        }
      />
    )
  } else if (view === 'empty') {
    // A member with no profile, or a profile with no positions, gets HTTP
    // 200 and an empty `holdings` array — a normal answer, not an error.
    body = (
      <EmptyState
        kind="empty"
        size="page"
        title="Nothing to analyse yet"
        description="Record a holding and this tab breaks its return down for you."
      />
    )
  } else if (overviewQuery.data === undefined || profitLossQuery.data === undefined) {
    body = null
  } else {
    const { summary, holdings } = overviewQuery.data
    // The hero is the server's own `/analytics/` breakdown (owner P4), not a sum made here.
    const returns =
      analyticsQuery.data === undefined
        ? undefined
        : totalReturnFromBreakdown(
            analyticsQuery.data.breakdown,
            summary.totalUnrealizedPlPercent,
            holdings,
          )
    const { adding, takingAway } = contributors(holdings)
    const sectors = sectorContribution(holdings)

    body = (
      // The main column's own 24px rhythm between the four modules (the mock opens them to
      // ~40, one of its page-by-page spacings; the canonical block gap is 24).
      <div className="flex flex-col gap-6">
        <TotalReturnCard
          returns={returns}
          error={analyticsQuery.isError}
          onRetry={() => {
            void analyticsQuery.refetch()
          }}
          retrying={analyticsQuery.isFetching}
        />

        {/* `@2xl` (672px), not `lg:` — this split lives inside the main
            column's `@container`, whose width is a function of the
            viewport AND the sidebar AND the Mitra panel. See
            `ScreenLayout`'s doc comment for the twelve real widths. */}
        <div className="grid grid-cols-1 gap-6 @2xl:grid-cols-2">
          <ContributorsCard adding={adding} takingAway={takingAway} />
          <SectorContributionTable rows={sectors} />
        </div>

        <ReturnMeasuresCard
          returns={analyticsQuery.data?.returns}
          pending={analyticsQuery.isPending}
          error={analyticsQuery.isError}
          onRetry={() => {
            void analyticsQuery.refetch()
          }}
          retrying={analyticsQuery.isFetching}
        />

        {/* No "Mitra says" banner: V2's Analytics frame (`4236:77871`) is the
            one Portfolio tab that draws none. The V1 build wrote its own
            copy for one while the frame was uncaptured; it is gone now
            that the frame is on disk. */}
      </div>
    )
  }

  return (
    <ScreenLayout
      header={<PortfolioFamilyHeader tab="analytics" compactAction />}
      pageTitle="Analytics"
    >
      {body}
    </ScreenLayout>
  )
}
